/* global NGX, log */
import {
  AllAnalyticsEvents as ALL,
  AnalyticsEvents as PUB,
  DeprecatedAnalyticsEvents as OLD,
} from '../../common/config/api.js';

import enums from '../../common/config/enums';

export default (function AnalyticsInsideIframe(w, d, $, _) {
  // add backbone events handler for Integration Events
  const IntegrationEvents = _.extend({}, Backbone.Events);

  // Integration API monitor for first user interaction with the form
  NGX.config.userHasInteracted = {};
  NGX.config.userHasViewed = false;

  /**
   * Send a message to the parent context handler outside the iframe context.
   * @param {String} eventName
   * @param {Object} params?
   */
  function integrationMessageParent(eventName, params) {
    if (NGX.App.state.framed) {
      log.debug('Messaging Parent with Integration Event: "' + eventName + '"', 6);
      const frameId = (function() {
        if (NGX.context.isPreview) {
          return 'ngxFrame-preview-details';
        }
        if (NGX.context.displayContainer) {
          return 'ngxFrame' + NGX.context.displayContainer.guid + ':' + NGX.context.displayContainer.id;
        }
        return '';
      })();

      const msg = {
        id:     frameId,
        event:  eventName,
        params: params || {},
        target: 'integration',
      };

      try {
        window.parent.postMessage(msg, '*');
      } catch (e) {
        log.error('Failed Messaging Parent with Integration Event: "' + eventName + '", ' + e.getMessage(), e);
      }
    }
  }

  /**
   * Initialise Analytics events bus
   */
  function initAnalytics() {
    // Some events need to be bridged over from Events -> IntegrationEvents, as
    // it becomes too complicated to manually add support. Please be careful since
    // each bus makes separate assumptions about payload (in practise this means
    // avoid passing params like the plague. It also creates subtle dependencies,
    // which is something you MUST avoid if you can just add an explicit:
    // NGX.Analytics.trigger(...).
    //
    // Add them below at your peril:
    const eventToIntegration = ['overlay:hidden'];

    eventToIntegration.forEach(function(event) {
      NGX.App.Events.on(event, function() {
        IntegrationEvents.trigger(event);
      });
    });

    // Services teams have requested that integration analytics events be made
    // available on the original NGX.App.Events bus. We have to exclude any
    // events that will cause a cycle, or that have duplicate triggers:
    //
    //  NGX.Analytics.trigger + NGX.App.Events.trigger in the same place
    const IntegrationToEventsBlacklist = [/^vote:/, /^overlay:/, /^page:navigate/, /^form:rating/].concat(
      eventToIntegration
    );

    IntegrationEvents.on('all', function(eventSignature, params) {
      // Ignore anything on the blacklist
      const blacklisted =
        IntegrationToEventsBlacklist.filter(function(regex) {
          return eventSignature.match(regex);
        }).length > 0;
      if (!blacklisted) {
        NGX.App.Events.trigger(eventSignature, params);
      }
    });

    window.addEventListener('message', function(event) {
      if (event.data.target === 'ping') {
        // Add the <frame id="" /> info to NGX.info. This is useful for sharing events information
        // Container is set to the generated <iframe id="..." />. There's nothing to stop the user from changing the
        // markup generated for them to paste into their site. To handle this, the ngx.embed's NGX object sends a ping
        // message, which tells the experience in the iframe what it's `id` is. This is assigned to NGX.info.container.
        //
        // This should not change anything, unless the user has been devious.
        NGX.info.container = event.data.id;
        return;
      }

      if (event.data.target !== 'integration') {
        return;
      }

      // External events should have `info` metadata attached
      event.data.params = event.data.params || {};
      event.data.params.info = event.data.params.info || {
        experienceId:      NGX.info.id,
        externalReference: NGX.info.externalReference,
        trackingReference: NGX.info.trackingReference,
      };

      IntegrationEvents.trigger(event.data.event, event.data.params, 'external');
    });

    // Buffer integration events while loading the experience
    var MAX_BUFFER = 100;
    var buffer = [];
    IntegrationEvents.on('all', function(eventSignature, payload) {
      if (buffer.length > MAX_BUFFER) {
        buffer = buffer.slice(1, MAX_BUFFER);
      }
      buffer.push([eventSignature, payload]);
    });

    // Allow some listeners to capture entire history
    IntegrationEvents.cold = function coldEventsListener(eventSignature, callback) {
      buffer
        .filter(function(e) {
          return e[0] === eventSignature;
        })
        .forEach(function(e) {
          callback(e[1]);
        });
      IntegrationEvents.on(eventSignature, callback);

      // Cleanup
      return function off() {
        IntegrationEvents.off(eventSignature, callback);
      };
    }.bind(IntegrationEvents);

    NGX.App.Events.on('privacy:consent:activated', function(payload) {
      if (payload.consentType === enums.PRIVACY_TYPES.ANALYTICS) {
        startAnalyticsCommunication();
        initIntegrations();
        // If the ngx cookie happens to exist when analytics gets enabled, set these values.
        const uid = NGX.StorageManager.get('ngx');
    		NGX.context.uid = uid;
        NGX.App.state.currentUser.id = uid;
      }
    });

    /** post-load events (document ready, when dom is ready) */
    $(function() {
      // Privacy Management: If we never set up this event listener or trigger the integration:bus:ready event,
      // the outside analytics will never fully init & so won't send analytics events.
      const allowAnalytics = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.ANALYTICS);
      if (!allowAnalytics) {
        // WYN-6333 Privacy consent - If we do not allow analytics cookies, then we must not track Analytics.
        return;
      }

      startAnalyticsCommunication();
    });
  }

  function startAnalyticsCommunication() {
    // Forward all integration events to the parent page as well.
    IntegrationEvents.on('all', function(eventName, params, source) {
      log.debug('[Integration -> ' + eventName + ']', 2);
      integrationMessageParent(eventName, params);
    });

    // Indicate the integration bus is ready for action...this will get sent to the parent also & finish the init there.
    IntegrationEvents.trigger('integration:bus:ready');
  }

  // Initiates handshake with integration lib.
  function initIntegrations() {
    const id = [0];

    function integrationHello(window, id) {
      // Privacy Management: Don't send hello to any analytics integrations if we don't have analytics consent.
      const allowAnalytics = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.ANALYTICS);
      if (!allowAnalytics) {
        // WYN-6333 Privacy consent - If we do not allow analytics cookies, then we must not track Analytics.
        return;
      }

      if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') {
        return;
      }
      window.dispatchEvent(new CustomEvent('ngx:integration:response', { detail: { id: id, NGX: NGX } }));
    }

    // Trigger a notification if any integration is already listening, and listen if one loads later
    window.addEventListener('ngx:integration', function() {
      integrationHello(window, id);
    });

    integrationHello(window, id);
  }

  function getAllowedEvent(eventName) {
    if (['*'].concat(ALL, OLD).indexOf(eventName) === -1) {
      console.error('Display does not support: "' + eventName + '", only:', PUB);
      return [];
    }
    return eventName === '*' ? ALL : [eventName];
  }

  /**
   * Helper method to create a eventListener which return a removeEventListener
   *
   * @param {function} subscribeFn
   * @param {function} unsubscribeFn
   * @param {string} eventName
   * @param {function} callback
   * @return {function}               - Remove the created event listener
   */
  function analyticsListen(subscribeFn, unsubscribeFn, eventName, callback) {
    const events = getAllowedEvent(eventName);

    const cleanup = _.map(events, function(event) {
      const cb = function(eventName, params) {
        callback(params, eventName);
      }.bind(null, event);

      subscribeFn(event, cb);

      return function() {
        unsubscribeFn(event, cb);
      };
    });

    return function removeAnalyticsListener() {
      _.forEach(cleanup, function(removeListener) {
        removeListener();
      });
    };
  }

  const onFn = IntegrationEvents.on.bind(IntegrationEvents);
  const onceFn = IntegrationEvents.once.bind(IntegrationEvents);
  const offFn = IntegrationEvents.off.bind(IntegrationEvents);

  /**
   * Subscribe a callback on the event occurring on the Analytics bus
   * @param {String} eventName
   * @param {function} callback
   * @return {function} removeListener
   */
  const on = analyticsListen.bind(null, onFn, offFn);

  /**
   * Subscribe a callback for the first occurance of an even on the Analytics bus
   * @param {String} eventName
   * @param {function} callback
   * @return {function} removeListener
   */
  const once = analyticsListen.bind(null, onceFn, offFn);

  /**
   * Subscribe a callback on the event occurring on the Analytics bus. This will replay any
   * events that may have occurred before subscription. Typically only used by integrations.
   * @param {String} eventName
   * @param {function} callback
   * @return {function} removeListener
   */
  function cold(eventName, callback) {
    const events = getAllowedEvent(eventName);

    const clean = _.map(events, function(event) {
      return IntegrationEvents.cold(
        event,
        function(eventName, params) {
          callback(params, eventName);
        }.bind(null, event)
      );
    });

    return function removeColdAnalyticsListeners() {
      _.forEach(clean, function(removeListener) {
        removeListener();
      });
    };
  }

  /**
   * Trigger an Analytics event - send to the analytics integrations
   * @param {String} eventName
   * @param {Object} params?
   */
  function trigger(eventName, params) {
    const allowAnalytics = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.ANALYTICS);
    if (!allowAnalytics) {
      // WYN-6333 Privacy consent - If we do not allow analytics cookies, then we must not track Analytics.
      return;
    }

    switch (eventName) {
      case PUB.FORM_MODULE_INTERACTION:
        {
          if (params.remoteFieldName in NGX.App.config.userHasInteracted) {
            // Once a module has been interacted with, we don't need any more
            // events for analytics.
            return;
          }
          NGX.App.config.userHasInteracted[params.remoteFieldName] = true;
        }
        break;

      case PUB.APP_VIEWPORT:
        {
          if (!NGX.appFlow.isStartRoute) {
            // The expectation is that this *only* triggers for the starting page,
            // not confirmation page, etc...
            return;
          }
          if (NGX.App.config.userHasViewed) {
            return;
          }
          NGX.App.config.userHasViewed = true;
        }
        break;
    }

    if (ALL.concat(OLD).indexOf(eventName) === -1) {
      console.error('Legacy does not support triggering: "' + eventName + '", only:', PUB);
      return;
    }
    // Events pushed outside the iframe need a way to reconcile the source
    // experience, this is it.  NGX.info is not useful outside as there could
    // be multiple campaign iframes in a page.
    const parameters = params || {};
    parameters.info = {
      experienceId:      NGX.info.id,
      trackingReference: NGX.info.trackingReference,
      externalReference: NGX.info.externalReference,
    };

    IntegrationEvents.trigger(eventName, parameters);
  }

  return Object.assign(
    {},
    PUB,
    { ALL_EVENTS: '*' },
    {
      init:             initAnalytics,
      initIntegrations: initIntegrations,
      on,
      once,
      cold,
      trigger,
    }
  );
})(window, document, $, _);
